import React, { useState } from 'react';
import Analytics from '../services/analytics';
import DoseTermsExplainer from '../components/DoseTermsExplainer';
import Syringe from '../components/SyringeSVG';
import { useQueryParams } from 'components/hooks/useQueryParams';
import { Link } from 'react-router-dom';
import TitleHelmet from 'components/TitleHelmet';
import DosageTable from 'components/DoseTableCheatSheet';
import OtherCalcs from 'components/OtherCalcs';
import 'css/tooltips.css';
// Import our new components
import { CalculatorInput, CalculatorWarning } from 'components/calc';
import { useCalculatorStorage } from 'components/hooks/useCalculatorStorage';

const OusiaDosageCalculator: React.FC = () => {
  const [showNote, setShowNote] = useState<boolean>(false);
  const [calculatedVolume, setCalculatedVolume] = useState<number | null>(null);
  const [showInputs, setShowInputs] = useState<boolean>(true);
  const { desiredDosage: queryDosage, ousiaVialDosage: queryVialDosage } = useQueryParams();

  // Use the custom storage hook
  const [vialDosage, setVialDosage] = useCalculatorStorage({
    key: 'vialDosage',
    initialValue: '',
    queryValue: queryVialDosage || undefined,
  });

  const [desiredDosage, setDesiredDosage] = useCalculatorStorage({
    key: 'desiredDosage',
    initialValue: '',
    queryValue: queryDosage || undefined,
  });

  const [concentration, setConcentration] = useCalculatorStorage({
    key: 'ousiaConcentration',
    initialValue: '',
  });

  // Check if concentration is within normal ranges
  const isConcentrationOutOfRange = (conc: number): boolean => {
    // For Ousia tirzepatide, check if outside 5-30mg/mL range
    return conc < 5 || conc > 30;
  };

  const handleCalculate = (): void => {
    const vialDosageValue = parseFloat(vialDosage);
    const concentrationValue = parseFloat((2 * vialDosageValue).toString());
    const desiredDosageValue = parseFloat(desiredDosage);

    if (concentrationValue > 0 && desiredDosageValue > 0) {
      const volume = desiredDosageValue / concentrationValue;
      setCalculatedVolume(volume);
      setConcentration(concentrationValue.toString());
      setShowInputs(false); // Collapse the input area

      // Scroll to top when calculation is submitted for better UX
      window.scrollTo({ top: 0, behavior: 'smooth' });

      Analytics.trackCalculation('Ousia', {
        vial_dosage: vialDosageValue,
        concentration: concentrationValue,
        desired_dosage: desiredDosageValue,
        result: volume,
      });
    } else {
      alert('Please enter valid positive values for both vial dose and your desired dosage');
    }
  };

  const handleShowInputs = (): void => {
    setShowInputs(true);
    setCalculatedVolume(null);
    setConcentration('');
  };

  return (
    <div className="App">
      <TitleHelmet
        title="Ousia Dosage Calculator"
        description="Calculate alternative tirzepatide doses from Ousia Pharmacy. Adjust dosages from the standard 50-unit vial with our free, simple calculator."
      />
      <header className="App-header">
        <h1>Ousia Tirzepatide Dose Calculator</h1>
      </header>

      {showInputs ? (
        <div
          className="calculator-container"
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter') handleCalculate();
          }}
        >
          <div className="input-container">
            <p>
              Got Tirzepatide from Ousia Pharmacy and want a different dose than shown on the vial? Ousia customizes the
              concentration so the labeled dosage equals 50 units. Use this calculator to find the units for your
              desired dose.
            </p>

            <CalculatorInput
              id="vialDosage"
              value={vialDosage}
              onChange={setVialDosage}
              unit="mg"
              placeholder="e.g., 15"
              label="Dosage shown on Vial (mg)"
              onClick={() => setShowNote(true)}
              isClickable={true}
            />

            {showNote && (
              <div className="tooltip">
                <p>
                  Remember to enter the dose listed on the vial, not your desired dose! Ousia uses a custom
                  concentration for each different dose, so we'll use this to determine the concentration in your vial.
                </p>
                <span onClick={() => setShowNote(false)} className="close-tooltip">
                  ✖
                </span>
              </div>
            )}

            <CalculatorInput
              id="desiredDosage"
              value={desiredDosage}
              onChange={setDesiredDosage}
              unit="mg"
              placeholder="e.g., 2.5"
              label="Desired Dosage (mg) -- this is the dose you want to take"
            />

            <p>
              <b>NOTE: THIS ONLY WORKS IF YOUR INSTRUCTIONS SAY TO USE 50 UNITS FOR THE DOSE ON THE LABEL.</b>
            </p>
            <button onClick={handleCalculate} className="calculate-button">
              Calculate
            </button>
          </div>
        </div>
      ) : (
        <span>
          &#9664;{' '}
          <Link className="smaller-text" onClick={handleShowInputs} to="">
            Reset calculator and start again
          </Link>
        </span>
      )}

      {calculatedVolume !== null && (
        <div className="result centered">
          <p>This vial has a concentration of {concentration} mg/mL.</p>
          {isConcentrationOutOfRange(parseFloat(concentration)) && (
            <CalculatorWarning
              message={`Warning: The calculated concentration of ${concentration}mg/mL is unusual for Ousia pharmacy. Please verify
              the dose indicated on the vial label that you entered on the previous screen.`}
            />
          )}
          For {desiredDosage}mg at {concentration}mg/mL concentration,
          {
            <div className="resultsText">
              you'd need {Math.round(calculatedVolume * 100)} units
              <br />
            </div>
          }{' '}
          {parseFloat(desiredDosage) > 15 && (
            <CalculatorWarning
              type="urgent"
              message={`Doses above 15mg are unusually high. Please verify your prescription and calculations carefully.`}
            />
          )}
          <Syringe calculatedVolume={calculatedVolume} />
          <p>
            For {desiredDosage}mg from a {vialDosage}mg Ousia vial, inject {Math.round(calculatedVolume * 100)} units (
            {calculatedVolume.toFixed(2)} mL) on a typical U-100 syringe.
          </p>
          <DosageTable concentration={concentration} targetDosage={desiredDosage} />
        </div>
      )}

      <div className="disclaimer">
        Disclaimer: This calculator is for informational purposes only and is not a substitute for professional medical
        advice. Always verify your dose with your healthcare provider. Fat Scientist is not responsible for any
        consequences from using this tool.
      </div>
      <OtherCalcs />

      <DoseTermsExplainer />
    </div>
  );
};

export default OusiaDosageCalculator;
